import { paymentChip } from '../planning/PlanningRdvCard';
import { avatarClass, memberInitials } from '../planning/planningPeople';
import { RDV_STATUS_UX } from '../../utils/equipeRdvRules';
import { rdvHasClientParticularity } from '../../utils/rdvClientNote';
import {
  clientDisplayName,
  formatParisClockColon,
  intervenantDisplayName,
  rdvDurationMinutes,
} from '../../utils/planningDayParis';

/** Colonnes fixes : heure · client · bloc métier (3 pistes) · paiement · statut */
const OUTER_GRID_CLASS =
  'grid-cols-[4.5rem_minmax(0,0.9fr)_minmax(0,2.6fr)_5.25rem_5.25rem]';

/** Prestation et professionnel en 1fr symétriques ; particularité au centre (piste fixe). */
const MIDDLE_TRIO_CLASS = 'grid-cols-[minmax(0,1fr)_7rem_minmax(0,1fr)]';
const MIDDLE_TRIO_GAP = 'gap-x-6';

/** Particularité seule — position visuelle validée (figée). Prestation / pro : ne pas déplacer. */
const PARTICULARITY_SHIFT = '-translate-x-16 lg:-translate-x-20';

function statusAccent(statut) {
  if (statut === 'EN_ATTENTE') return 'bg-amber-400';
  if (statut === 'EN_ATTENTE_PAIEMENT') return 'bg-orange-400';
  if (statut === 'CONFIRME') return 'bg-emerald-500';
  if (statut === 'ABSENT') return 'bg-rose-400';
  if (statut === 'TERMINE') return 'bg-gray-300';
  return 'bg-violet-400';
}

function formatDuration(minutes) {
  if (!minutes) return '—';
  if (minutes % 60 === 0) {
    const h = minutes / 60;
    return h === 1 ? '1 h' : `${h} h`;
  }
  if (minutes >= 60 && minutes % 30 === 0) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m ? `${h} h ${m} min` : `${h} h`;
  }
  return `${minutes} min`;
}

function ParticularityCell({ rdv }) {
  if (!rdvHasClientParticularity(rdv)) {
    return null;
  }
  return (
    <span
      className="inline-flex h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-red-100"
      title="Particularité renseignée"
      aria-label="Particularité renseignée"
    />
  );
}

function DayQueueRow({ rdv, member, onRdvClick }) {
  const statut = String(rdv?.statut || '').toUpperCase();
  const statusMeta = RDV_STATUS_UX[statut] || { label: statut || '—', className: 'bg-gray-100 text-gray-700' };
  const pay = paymentChip(rdv);
  const proLabel = member
    ? intervenantDisplayName(member)
    : rdv.instructeur
      ? intervenantDisplayName(rdv.instructeur)
      : rdv.instructeurId
        ? 'Intervenant'
        : 'Non assigné';
  const proKey = rdv.instructeurId || 'none';
  const duration = rdvDurationMinutes(rdv);

  return (
    <button
      type="button"
      onClick={() => onRdvClick?.(rdv)}
      className="group flex w-full min-w-0 items-stretch gap-0 rounded-xl border border-gray-100 bg-white text-left shadow-[0_1px_2px_rgba(15,23,42,0.03)] transition hover:border-violet-100 hover:bg-violet-50/30"
    >
      <span className={`w-1 shrink-0 rounded-l-xl ${statusAccent(statut)}`} aria-hidden />
      <span
        className={`grid min-w-0 flex-1 ${OUTER_GRID_CLASS} grid-rows-1 items-center gap-x-3 gap-y-0 px-3 py-2.5 text-xs`}
      >
        <span className="min-w-0 truncate font-semibold tabular-nums text-gray-900">
          {formatParisClockColon(rdv.dateDebut)}
        </span>
        <span className="min-w-0 truncate font-semibold text-gray-900">
          {clientDisplayName(rdv.user)}
        </span>

        <span className={`grid min-w-0 ${MIDDLE_TRIO_CLASS} ${MIDDLE_TRIO_GAP} items-center`}>
          <span className="min-w-0 truncate pr-1 text-gray-600">
            {rdv.prestation?.nom || 'Prestation'}
            <span className="text-gray-400"> · {formatDuration(duration)}</span>
          </span>
          <span className={`flex items-center justify-center ${PARTICULARITY_SHIFT}`}>
            <ParticularityCell rdv={rdv} />
          </span>
          <span className="flex min-w-0 items-center gap-1.5 truncate pl-1 text-gray-600">
            <span
              className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[9px] font-bold ${avatarClass(proKey)}`}
            >
              {member ? memberInitials(member) : proLabel.slice(0, 2).toUpperCase()}
            </span>
            <span className="truncate">{proLabel}</span>
          </span>
        </span>

        <span className="min-w-0 truncate text-right">
          {pay ? (
            <span className={`inline-block max-w-full truncate rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${pay.className}`}>
              {pay.label}
            </span>
          ) : (
            <span className="text-gray-400">—</span>
          )}
        </span>
        <span className="min-w-0 truncate text-right">
          <span className={`inline-block max-w-full truncate rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${statusMeta.className}`}>
            {statusMeta.label}
          </span>
        </span>
      </span>
    </button>
  );
}

export default function AujourdHuiDayQueueRows({ rdvs, memberByInstructeurId, onRdvClick, loading }) {
  if (loading) {
    return (
      <div className="flex h-32 items-center justify-center rounded-xl border border-gray-100 bg-white">
        <div className="h-7 w-7 animate-spin rounded-full border-b-2 border-violet-600" />
      </div>
    );
  }

  if (!rdvs?.length) {
    return (
      <p className="rounded-xl border border-dashed border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500">
        Aucun rendez-vous aujourd&apos;hui.
      </p>
    );
  }

  return (
    <div className="space-y-1.5">
      <div
        className={`hidden min-w-0 px-3 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 lg:grid ${OUTER_GRID_CLASS} lg:grid-rows-1 lg:gap-x-3 lg:gap-y-0 lg:pl-4`}
        aria-hidden
      >
        <span className="min-w-0 truncate">Heure</span>
        <span className="min-w-0 truncate">Client</span>
        <span className={`grid min-w-0 ${MIDDLE_TRIO_CLASS} ${MIDDLE_TRIO_GAP} items-end`}>
          <span className="truncate">Prestation</span>
          <span className={`text-center text-[9px] leading-tight ${PARTICULARITY_SHIFT}`}>Particularité</span>
          <span className="truncate text-[9px] leading-tight">Professionnel</span>
        </span>
        <span className="min-w-0 truncate text-right">Paiement</span>
        <span className="min-w-0 truncate text-right">Statut</span>
      </div>
      {rdvs.map((rdv) => (
        <DayQueueRow
          key={rdv.id}
          rdv={rdv}
          member={rdv.instructeurId ? memberByInstructeurId[rdv.instructeurId] : null}
          onRdvClick={onRdvClick}
        />
      ))}
    </div>
  );
}
